/**
 * A tab that was already open when a View as started or ended in another tab (review finding 5).
 *
 * The cookie jar is shared, so that tab silently runs as the other person while it still shows the old shell and no banner.
 * Two ways to notice, either one reloads the page (it then boots from `/auth/session/` again, and shows or drops the bar):
 *  - every API answer carries `X-View-As` (`0` for a normal session, the session's end time for a View as);
 *  - the tab that starts or ends a View as says so on a BroadcastChannel.
 *
 * No React and no translator in here: `api-client` loads this module, and the exam runner loads that.
 */

export const VIEW_AS_HEADER = 'X-View-As'
export const VIEW_AS_CHANNEL = 'edv-view-as'

/** The one place the page reloads itself (a seam the tests replace). */
export const tab = {
  reload() {
    window.location.reload()
  },
}

let seen: string | null = null

/** Remembers the first `X-View-As` this page saw and reloads when a later answer differs. Answers without the header are ignored. */
export function noteViewAsResponse(response: { headers?: { get(name: string): string | null } } | null | undefined) {
  const value = response?.headers?.get(VIEW_AS_HEADER)
  if (typeof value !== 'string' || value === '') return
  if (seen === null) {
    seen = value
    return
  }
  if (value !== seen) {
    seen = value
    tab.reload()
  }
}

/** Forgets what the page saw (tests). */
export function resetViewAsSeen() {
  seen = null
}

type Channel = Pick<BroadcastChannel, 'postMessage' | 'addEventListener' | 'removeEventListener'> & { close?: () => void }
const defaultFactory = (): Channel | null => (typeof BroadcastChannel === 'function' ? new BroadcastChannel(VIEW_AS_CHANNEL) : null)
let channel: Channel | null | undefined
let factory: () => Channel | null = defaultFactory

function openChannel(): Channel | null {
  if (channel === undefined) {
    try {
      channel = factory()
    } catch {
      channel = null
    }
  }
  return channel
}

/** Replaces how the channel is made and forgets the current one (tests). */
export function resetViewAsChannel(next?: () => Channel | null) {
  channel?.close?.()
  channel = undefined
  factory = next ?? defaultFactory
}

/** Tells the other tabs where this one stands: the end time of the View as it runs, or `'0'` for a normal session. */
export function announceViewAs(value: string) {
  try {
    openChannel()?.postMessage({ value })
  } catch {
    // No channel: the header on the next API answer does the same job.
  }
}

/** Calls `handler(value)` for what another tab announced. Returns the unsubscribe function. */
export function listenViewAs(handler: (value: string) => void): () => void {
  const open = openChannel()
  if (!open) return () => undefined
  const onMessage = (event: Event) => handler(String((event as MessageEvent<{ value?: unknown }>).data?.value ?? ''))
  open.addEventListener('message', onMessage)
  return () => open.removeEventListener('message', onMessage)
}
